import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { useNotificationStore } from '../stores/notificationStore';
import { useMembers, memberName } from '../stores/lookupStore';
import { PMNotification } from '../types/pm';

/**
 * 通知：被指派任務、任務有新留言（點一下標為已讀並打開該任務）
 * 列表型內容用單一容器＋細線分隔（R13）；未讀以主題色圓點＋粗體標示
 */
const NotificationsPage: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const navigate = useNavigate();
  const { items, unread, markRead, hasMore, loadingMore, loadMore } = useNotificationStore();
  const members = useMembers();

  const open = (notification: PMNotification) => {
    if (!notification.readAt) markRead([notification.id]);
    navigate(notification.link);
  };

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('pmNotifications.title', { ns: 'pages' })}</h1>
          <div className="flex space-x-3">
            <Button variant="secondary" onClick={() => markRead()} disabled={unread === 0}>
              {t('pmNotifications.markAllRead', { ns: 'pages' })}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-lg border border-line overflow-hidden">
        {items.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-medium text-fg">{t('pmNotifications.empty', { ns: 'pages' })}</p>
            <p className="mt-1 text-[13px] text-fg-secondary">{t('pmNotifications.emptyHint', { ns: 'pages' })}</p>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {items.map(notification => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => open(notification)}
                  className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <span
                    aria-hidden="true"
                    className={`h-2 w-2 shrink-0 rounded-full ${notification.readAt ? 'bg-transparent' : 'bg-accent-fill'}`}
                  />
                  <span className={`flex-1 min-w-0 text-sm truncate ${notification.readAt ? 'text-fg-secondary' : 'text-fg font-medium'}`}>
                    {t(`pmNotifications.types.${notification.type}`, {
                      ns: 'pages',
                      actor: memberName(members, notification.actorId) || t('pmNotifications.someone', { ns: 'pages' }),
                      title: notification.title,
                    })}
                  </span>
                  <span className="text-[13px] text-fg-secondary tabular-nums shrink-0">{formatTableDate(notification.createdAt, i18n.language)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* 載入更多（主流通知中心的做法：照時間往前看，不用頁碼） */}
        {hasMore && items.length > 0 && (
          <div className="border-t border-line px-5 py-3 text-center">
            <button type="button" onClick={loadMore} disabled={loadingMore} className="text-sm text-accent hover:underline disabled:opacity-50">
              {t(loadingMore ? 'common.status.loading' : 'pmNotifications.loadMore', { ns: loadingMore ? 'translation' : 'pages' })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
